import { Button, DialogActions, DialogContent, FormControl, Grid, Typography } from '@mui/material';
import { DataGrid, gridClasses, GridColDef } from '@mui/x-data-grid';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../store/store';
import { RootState } from '../../reducers';
import { setActiveStep, setOpenAddMember, setImportSuccessful, uploadMember, exportUploadedMemberDetail } from '../../store/fuhfum.slice';
import { localDateFormat } from '../../utils/timeFormat';
import LinearProgressWithLabel from '../Shared/ProgressBar';
import { isDefaultDate, generateRandomId } from '../../utils/common';
import b64toBlob from 'b64-to-blob';
import { useState } from 'react';

const ImportData = (props) => {
    const { fileProcessedCount, importSuccessful, updatingMember, validatedMembers, uploadedMemberDetail } = useSelector((state: RootState) => state.fuhfum);
    const { file, setFile } = props;
    const dispatch = useAppDispatch();
    const newMembers = validatedMembers.filter((m) => !m.IsAlreadyExisting);
    const existingMembers = validatedMembers.filter((m) => m.IsAlreadyExisting);
    const [downLoading, setDownLoading] = useState<boolean>(false);

    const newColumns: GridColDef[] = [
        {
            field: 'MemberId',
            headerName: 'Member Id',
            minWidth: 160,
        },
        {
            field: 'AhcccsId',
            headerName: 'AHCCCS Id',
            minWidth: 120,
        },
        {
            field: 'FirstName',
            headerName: 'First Name',
            minWidth: 160,
        },
        {
            field: 'MiddleName',
            headerName: 'Middle Name',
            minWidth: 160,
        },
        {
            field: 'LastName',
            headerName: 'Last Name',
            minWidth: 160,
        },
        {
            field: 'DOB',
            headerName: 'DOB',
            minWidth: 100,
            valueGetter: (value, row) => `${isDefaultDate(row.DOB) ? '-' : localDateFormat(new Date(row.DOB))}`,
        },
        {
            field: 'Gender',
            headerName: 'Gender',
            minWidth: 100,
        },
        {
            field: 'Address1',
            headerName: 'Address1',
            minWidth: 230,
            flex: 1,
        },
        {
            field: 'Address2',
            headerName: 'Address2',
            minWidth: 220,
            flex: 1,
        },
        {
            field: 'City',
            headerName: 'City',
            minWidth: 160,
        },
        {
            field: 'ZipCode',
            headerName: 'ZipCode',
            minWidth: 100,
        },
        {
            field: 'PcpName',
            headerName: 'PCP Name',
            minWidth: 250,
            flex: 1,
        },
        {
            field: 'FuhFumType',
            headerName: 'Type',
            minWidth: 250,
            flex: 1,
        },
    ];
    const existingColumns: GridColDef[] = [
        {
            field: 'MemberId',
            headerName: 'Member Id',
            minWidth: 160,
        },
        {
            field: 'AhcccsId',
            headerName: 'AHCCCS Id',
            minWidth: 120,
        },
        {
            field: 'FirstName',
            headerName: 'First Name',
            minWidth: 160,
        },
        {
            field: 'MiddleName',
            headerName: 'Middle Name',
            minWidth: 160,
        },
        {
            field: 'LastName',
            headerName: 'Last Name',
            minWidth: 160,
        },
        {
            field: 'DOB',
            headerName: 'DOB',
            minWidth: 100,
            valueGetter: (value, row) => `${isDefaultDate(row.DOB) ? '-' : localDateFormat(new Date(row.DOB))}`,
        },
        {
            field: 'Status',
            headerName: 'Status',
            minWidth: 150,
        },
        {
            field: 'Population',
            headerName: 'Population',
            minWidth: 140,
        },
        {
            field: 'Gender',
            headerName: 'Gender',
            minWidth: 100,
        },
        {
            field: 'Address1',
            headerName: 'Address1',
            minWidth: 200,
            flex: 1,
        },
        {
            field: 'Address2',
            headerName: 'Address2',
            minWidth: 200,
            flex: 1,
        },
        {
            field: 'City',
            headerName: 'City',
            minWidth: 160,
        },
        {
            field: 'ZipCode',
            headerName: 'ZipCode',
            minWidth: 100,
        },
        {
            field: 'PcpName',
            headerName: 'PCP Name',
            minWidth: 250,
            flex: 1,
        },
        {
            field: 'FuhFumType',
            headerName: 'Type',
            minWidth: 250,
            flex: 1,
        },
    ];

    const exportMemberDetail = async () => {
        setDownLoading(true);
        const data = await exportUploadedMemberDetail();
        let blob = b64toBlob(data.base64String, 'text/csv');
        let url = window.URL.createObjectURL(blob);
        let link = document.createElement('a');
        link.href = url;
        var date = new Date();
        var mm = date.getMonth() + 1; // getMonth() is zero-based
        var dd = date.getDate();
        var dateInfo = [(mm > 9 ? '' : '0') + mm, (dd > 9 ? '' : '0') + dd, date.getFullYear()].join('');
        link.setAttribute('download', `FUHFUM Uploaded Member Summary - ${dateInfo}.xlsx`);
        link.click();
        link.remove();
        setDownLoading(false);
    };

    return (
        <>
            <LinearProgressWithLabel value={fileProcessedCount.Percentage} />
            {importSuccessful && (
                <DialogContent>
                    <FormControl fullWidth>
                        <Grid spacing={2} container p={2} sx={{ height: '65vh' }}>
                            <Grid size={6} textAlign="center" marginX={'auto'}>
                                <Typography variant="h4">Your file was imported successfully.</Typography>
                                <Typography variant="h5" marginTop={4}>
                                    Imported Members Summary
                                </Typography>
                                {Object.entries(uploadedMemberDetail).map(([label, value], index) => (
                                    <Typography key={index} className="flex justify-between border-b pb-2">
                                        <span className="font-medium" style={{ paddingRight: '10px' }}>
                                            {label} -
                                        </span>
                                        <span className="font-bold">{value}</span>
                                    </Typography>
                                ))}
                                <div style={{ paddingTop: '15px' }}>
                                    <Button
                                        style={{ minWidth: '120px', marginLeft: 'auto' }}
                                        onClick={() => exportMemberDetail()}
                                        variant="contained"
                                        color="success"
                                        disabled={downLoading}
                                    >
                                        {downLoading ? 'Exporting..' : 'Export'}
                                    </Button>
                                </div>
                                {!updatingMember && (
                                    <Button
                                        variant="contained"
                                        className="button-120"
                                        sx={{ marginTop: 3 }}
                                        onClick={() => {
                                            setFile(null);
                                            dispatch(setImportSuccessful(false));
                                            dispatch(setActiveStep(0));
                                        }}
                                    >
                                        Continue
                                    </Button>
                                )}
                                {updatingMember && (
                                    <Button
                                        variant="contained"
                                        className="button-120"
                                        sx={{ marginTop: 3 }}
                                        onClick={() => {
                                            dispatch(setOpenAddMember(false));
                                            setFile(null);
                                        }}
                                    >
                                        Finish
                                    </Button>
                                )}
                            </Grid>
                        </Grid>
                    </FormControl>
                </DialogContent>
            )}
            {!importSuccessful && (
                <>
                    <DialogContent>
                        <FormControl fullWidth>
                            <Grid spacing={2} container p={2} sx={{ height: '70vh' }}>
                                <Grid size={12}>
                                    <Typography variant="h4" marginBottom={3}>
                                        Review & import your data
                                    </Typography>
                                </Grid>
                                <Grid size={6}>
                                    <Typography textAlign="center" variant="h6">
                                        New Members
                                    </Typography>
                                    <div style={{ height: '55vh', width: '100%' }}>
                                        <DataGrid
                                            rows={newMembers}
                                            columns={newColumns}
                                            pageSizeOptions={[5]}
                                            getRowId={(row) => `${row.AhcccsId}-${generateRandomId()}`}
                                            getRowHeight={() => 'auto'}
                                            sx={{
                                                [`& .${gridClasses.cell}`]: {
                                                    py: 1,
                                                },
                                            }}
                                        />
                                    </div>
                                </Grid>
                                <Grid size={6}>
                                    <Typography textAlign="center" variant="h6">
                                        Existing Members
                                    </Typography>
                                    <div style={{ height: '55vh', width: '100%' }}>
                                        <DataGrid
                                            rows={existingMembers}
                                            columns={existingColumns}
                                            pageSizeOptions={[5]}
                                            getRowId={(row) => `${row.AhcccsId}-${generateRandomId()}`}
                                            getRowHeight={() => 'auto'}
                                            sx={{
                                                [`& .${gridClasses.cell}`]: {
                                                    py: 1,
                                                },
                                            }}
                                        />
                                    </div>
                                </Grid>
                            </Grid>
                        </FormControl>
                    </DialogContent>

                    <DialogActions>
                        <Button className="button-120" variant="contained" onClick={() => dispatch(setActiveStep(1))}>
                            Previous
                        </Button>
                        <Button
                            className="button-120"
                            variant="contained"
                            disabled={validatedMembers.length === 0}
                            onClick={() => dispatch(uploadMember(file))}
                        >
                            Import
                        </Button>
                    </DialogActions>
                </>
            )}
        </>
    );
};

export default ImportData;
